import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import api from '@/services/api';
import { isReadOnlyUser } from '@/hooks/useReadOnly';

interface ChartColorsContextValue {
  /** Current global color map: chartTemplateId → colors[] */
  colors: Record<string, string[]>;
  /** Update colors for one chart — saved to the dedicated DB table immediately */
  setChartColors: (chartId: string, colors: string[]) => void;
  /** Whether colors have been loaded from the server */
  isLoaded: boolean;
  /** Force refresh colors from server */
  refreshColors: () => Promise<void>;
}

const ChartColorsContext = createContext<ChartColorsContextValue>({
  colors: {},
  setChartColors: () => {},
  isLoaded: false,
  refreshColors: async () => {},
});

export const ChartColorsProvider = ({ children }: { children: React.ReactNode }) => {
  const [colors, setColors] = useState<Record<string, string[]>>({});
  const [isLoaded, setIsLoaded] = useState(false);
  const lastTokenRef = useRef<string | null>(null);
  const isLoadedRef = useRef(false);

  // Keep ref in sync with state
  useEffect(() => {
    isLoadedRef.current = isLoaded;
  }, [isLoaded]);

  // Function to fetch colors from API
  const fetchColors = useCallback(async (force = false) => {
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    if (!token) {
      // No token - reset state and wait for login
      setIsLoaded(false);
      lastTokenRef.current = null;
      return;
    }

    // If same token and already loaded, skip (unless forced)
    if (!force && lastTokenRef.current === token && isLoadedRef.current) {
      return;
    }

    try {
      const res = await api.getChartColors();
      const data: Record<string, string[]> = (res as any)?.data ?? res ?? {};
      setColors(data);
      lastTokenRef.current = token;
    } catch {
      // Ignore errors
    } finally {
      setIsLoaded(true);
    }
  }, []); // No dependencies - uses refs for state checks

  // Poll for token changes - this catches when user logs in
  useEffect(() => {
    const checkToken = () => {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      // If token changed (including from null to a value), fetch colors
      if (token && token !== lastTokenRef.current) {
        fetchColors(true); // Force refresh since token changed
      } else if (!token && lastTokenRef.current) {
        // Token was removed (logout)
        setColors({});
        setIsLoaded(false);
        lastTokenRef.current = null;
      }
    };

    // Check immediately
    checkToken();

    // Also check periodically to catch login events
    const interval = setInterval(checkToken, 500);

    // Listen for storage events (in case token changes in another tab)
    window.addEventListener('storage', checkToken);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', checkToken);
    };
  }, [fetchColors]);

  const setChartColors = useCallback((chartId: string, newColors: string[]) => {
    setColors((prev) => {
      const updated = { ...prev };
      if (newColors.length === 0) delete updated[chartId];
      else updated[chartId] = newColors;
      return updated;
    });

    // Persist immediately to the dedicated DB table — no debounce needed,
    // the upsert is cheap and ensures all views see the same color instantly.
    if (newColors.length === 0 || isReadOnlyUser()) return;
    api.setChartColor(chartId, newColors).catch(() => {});
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    colors,
    setChartColors,
    isLoaded,
    refreshColors: fetchColors
  }), [colors, setChartColors, isLoaded, fetchColors]);

  return (
    <ChartColorsContext.Provider value={contextValue}>
      {children}
    </ChartColorsContext.Provider>
  );
};

export const useChartColors = () => useContext(ChartColorsContext);
